import { ChangeEvent, memo, useContext } from "react";
import { useState } from "react";
import { Controller, RegisterOptions } from "react-hook-form";
import { MainThemeContext } from "../../context/ThemeProvider/ThemeProvider";
// import { DatePickerProps } from "react-datepicker";
// -------- icons -------
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker, DatePickerProps } from "@mui/x-date-pickers/DatePicker";
// import ClearIcon from "@mui/icons-material/Clear";
// import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import {
  Autocomplete,
  AutocompleteProps,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  RadioGroupProps,
  TextField,
  TextFieldProps,
  FormControlLabelProps,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
const textColorClass = "text-grey-500";
export type Radios = {
  label: string | number;
  value: string | number | boolean;
};

type CustomFormFieldType = {
  control: any;
  name: string;
  fieldProps: TextFieldProps;
  rules: RegisterOptions;
  element: "input" | "radio" | "autocomplete" | "checkbox" | "datepicker";
};

export type CustomAutocompleteType = CustomFormFieldType & {
  options: any[];
  loading?: boolean;
  autocompleteProps?: Omit<
    AutocompleteProps<any, boolean, boolean, boolean>,
    "renderInput" | "options"
  >;
};

type CustomRadioType = CustomFormFieldType & {
  radioProps: TextFieldProps & RadioGroupProps;
  radios: Radios[];
};

type CustomCkeckBoxType = CustomFormFieldType & {
  checkboxProps?: Omit<FormControlLabelProps, "control">;
};

type CustomDatePickerType = CustomFormFieldType & {
  calenderProps?: Omit<
    DatePickerProps<Dayjs>, // Pass Dayjs as the date type
    | "className"
    | "onSelect"
    | "dropdownMode"
    | "setOpen"
    | "dateFormat"
    | "preSelection"
    | "setPreSelection"
    | "selectsRange"
    | "handleOnKeyDown"
    | "onChange"
    | "selectsMultiple"
    | "excludeScrollbar"
  >;
  dateFormat: "DD-MM-YYYY" | "YYYY-MM-DD" | "YYYY-DD-MM";
};

export type TextFieldType = Omit<CustomFormFieldType, "control">;
export type RadioType = Omit<CustomRadioType, "control">;
export type AutocompleteType = Omit<
  CustomAutocompleteType,
  "control" | "options"
>;
export type DatePickerType = Omit<CustomDatePickerType, "control">;
export type CheckboxType = Omit<CustomCkeckBoxType, "control">;

const types = ["date", "time", "datetime-local"];
export type PropType =
  | CustomFormFieldType
  | CustomAutocompleteType
  | CustomRadioType
  | CustomCkeckBoxType
  | CustomDatePickerType;
export const maxStringSize = 2000;
const CustomFormField = memo((props: PropType) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [fieldError, setFieldError] = useState<string>("");
  const { control, name, rules, element, fieldProps } = props;
  const { customTheme } = useContext(MainThemeContext);
  return (
    <Controller
      control={control}
      name={name}
      rules={{...rules,maxLength: fieldProps?.InputProps?.inputProps?.maxLength || maxStringSize}}
      render={({
        field: { ref, onChange, value = null, ...field},
        fieldState: { error },
      }) => {
        if (element == "input") {
          return (
            <TextField
              {...fieldProps}
              {...field}
              label={
                <span className={textColorClass}>
                  {fieldProps.label}
                  {rules?.required && <span className="text-red-500">*</span>}
                </span>
              }
              error={!!error || Boolean(fieldError)}
              helperText={error?.message || fieldError}
              type={showPassword ? "text" : fieldProps.type}
              value={value || ""}
              onChange={(
                e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                onChange(e.target.value);
                value?.length >
                (fieldProps.InputProps?.inputProps?.maxLength || maxStringSize)
                  ? setFieldError(`Maximum characters ${maxStringSize}`)
                  : setFieldError("");
              }}
              InputProps={{
                ...fieldProps.InputProps,
                endAdornment:
                  fieldProps.type === "password" ? (
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  ) : (
                    fieldProps.InputProps?.endAdornment || <></>
                  ),
                inputProps: {
                  maxLength: maxStringSize, // Set the max length here
                  ...fieldProps.InputProps?.inputProps,
                },
              }}
              InputLabelProps={
                types.includes(fieldProps.type as string)
                  ? { shrink: true }
                  : {}
              }
            />
          );
        }
        if (element == "radio") {
          return (
            <div className="flex flex-col">
              <div className="flex flex-row gap-2 items-center">
                <FormLabel
                  error={!!error}
                  id={`demo-radio-buttons-group-${fieldProps.label}`}
                >
                  <span className="text-sm">
                    {fieldProps.label}
                    {rules?.required && <span className="text-red-500">*</span>}
                  </span>
                </FormLabel>
                {!!error && (
                  <FormHelperText>
                    <span className="text-red-500">{error.message}</span>
                  </FormHelperText>
                )}
              </div>
              <RadioGroup
                {...(props as CustomRadioType).radioProps}
                {...field}
                value={value}
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                aria-labelledby={`demo-radio-buttons-group-${fieldProps.label}`}
              >
                {(props as CustomRadioType).radios?.map((radio, index) => (
                  <FormControlLabel
                    key={index.toString() + radio.label}
                    value={radio.value}
                    disabled={Boolean(fieldProps?.disabled)}
                    control={<Radio size="small" />}
                    label={
                      <span className={textColorClass + " text-sm"}>
                        {radio.label}
                      </span>
                    }
                  />
                ))}
              </RadioGroup>
            </div>
          );
        }
        if (element == "autocomplete") {
          return (
            <Autocomplete
              {...field}
              {...(props as CustomAutocompleteType).autocompleteProps}
              options={(props as CustomAutocompleteType).options || []}
              value={value || null}
              onChange={(_, data) => {
                onChange(data || null);
              }}
              loading={(props as CustomAutocompleteType)?.loading}
              renderInput={(params) => (
                <TextField
                  inputRef={ref}
                  {...params}
                  {...field}
                  {...fieldProps}
                  label={
                    <span className={textColorClass}>
                      {fieldProps.label}{" "}
                      {rules.required && (
                        <span className="text-red-500">*</span>
                      )}
                    </span>
                  }
                  error={!!error}
                  helperText={error?.message || ""}
                />
              )}
            />
          );
        }
        if (element == "checkbox") {
          return (
            <FormControlLabel
              sx={{ width: "fit-content" }}
              {...(props as CustomCkeckBoxType).checkboxProps}
              label={
                <span
                  className={
                    !!error
                      ? "text-red-500 text-sm"
                      : textColorClass + " text-sm"
                  }
                >
                  {fieldProps.label}{" "}
                  {rules.required && <span className="text-red-500">*</span>}
                </span>
              }
              control={
                <Checkbox
                  onChange={onChange}
                  checked={value || false}
                  {...field}
                />
              }
            />
          );
        }
        if (element == "datepicker") {
          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                {...(props as CustomDatePickerType).calenderProps}
                onChange={(date) => {
                  if(dayjs(date).format("YYYY-MM-DD") === "Invalid Date"){
                    onChange(null);
                  }else{
                    onChange(dayjs(date).format("YYYY-MM-DD"))
                  }
                }}
                value={dayjs(value)}
                label={
                  props.rules.required ? (
                    <span>
                      {props.fieldProps.label}{" "}
                      <span className="text-red-500">*</span>
                    </span>
                  ) : (
                    props.fieldProps.label
                  )
                }
                format={customTheme.dateFormat}
                slotProps={{
                  textField: {
                    placeholder: (props as CustomDatePickerType).dateFormat,
                    error: !!error,
                    helperText: error?.message,
                  },
                }}
              />
            </LocalizationProvider>
          );
        }
        return <div></div>;
      }}
    />
  );
});

export { CustomFormField };